import { Component } from 'react';
import { createRefetchContainer, graphql } from 'react-relay/legacy.js';
import { designerDirectoryRootQuery } from './designerDirectoryRootQuery.js';
import { DesignerDirectoryHeader } from './components/DesignerDirectoryHeader.jsx';
import { DesignerDirectoryContent } from './components/DesignerDirectoryContent.tsx';
import { DesignerDirectoryContactModal } from './components/DesignerDirectoryContactModal.jsx';
import { queryParamActions } from './helpers/designerDirectoryQueryParams.js';
import { getResultsCountForPage, getOffsetForPage } from './helpers/paginationValues.ts';
import { trackPageview } from './helpers/designerDirectoryTracking.js';
import { loadingIntents } from '../shared/index.ts';
import styles from './DesignerDirectoryWrapper.scss';
class DesignerDirectoryWrapperComponent extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loadingIntent: props.initialLoading ? loadingIntents.INITIAL_LOAD : null,
            pendingLoadingIntent: null,
            currentRefetch: null,
            error: props.initialError,
            isContactModalOpen: false,
            contactModalRecipient: null,
        };
    }

    componentDidMount() {
        trackPageview();
    }

    componentDidUpdate(prevProps) {
        const initialLoadingFinished = prevProps.initialLoading && !this.props.initialLoading;
        const hasInitialError = !prevProps.initialError && this.props.initialError;

        if (initialLoadingFinished) {
            if (this.state.pendingLoadingIntent) {
                this.setState(
                    {
                        loadingIntent: this.state.pendingLoadingIntent,
                        pendingLoadingIntent: null,
                    },
                    () => this.refetch(this.state.pendingLoadingIntent)
                );
            } else {
                this.setState({
                    loadingIntent: null,
                });
            }
        } else if (hasInitialError) {
            this.setState({
                error: this.props.initialError,
            });
        } else if (prevProps.page !== this.props.page) {
            this.refetch(loadingIntents.PAGE_CHANGE);
        } else if (
            prevProps.sortBy !== this.props.sortBy ||
            prevProps.locationFilter !== this.props.locationFilter
        ) {
            this.refetch();
        }
    }

    onPageChange = page => {
        window.scrollTo({
            top: 0,
            left: 0,
            behavior: 'instant',
        });
        this.props.onQueryParamsChange(queryParamActions.CHANGE_PAGE, page);
    };
    onLocationFilterChange = locationFilter => {
        this.props.onQueryParamsChange(queryParamActions.CHANGE_LOCATION_FILTER, locationFilter);
    };
    onSortByChange = sortBy => {
        this.props.onQueryParamsChange(queryParamActions.CHANGE_SORT_BY, sortBy);
    };
    onContactClick = contactModalRecipient => {
        this.setState({
            contactModalRecipient,
            isContactModalOpen: true,
        });
    };
    refetch = intent => {
        // user tried triggering refetch before initial load was complete; add
        // refetch to pending, overwriting the previous if there was one and return
        if (this.state.loadingIntent === loadingIntents.INITIAL_LOAD) {
            this.setState({
                pendingLoadingIntent: intent || loadingIntents.HARD_REFRESH,
            });
            return;
        }

        if (this.state.currentRefetch) {
            this.state.currentRefetch.dispose();
        }

        if (this.props.relay) {
            const pageToLoad = intent === loadingIntents.PAGE_CHANGE ? this.props.page : 1;
            const regionCodes = this.props.locationFilter;
            const sortBy = this.props.sortBy;
            const showOnlyTpnFirms = this.props.showOnlyTpnFirms;
            const refetchVariables = {
                page: pageToLoad,
                resultsPerPage: getResultsCountForPage(pageToLoad),
                pageStartOffset: getOffsetForPage(pageToLoad),
                sortBy: sortBy,
                regionCodes,
                showOnlyTpnFirms,
                search: '',
                hasSearch: false,
            };

            const onComplete = e =>
                this.setState(
                    {
                        error: !!e,
                        loadingIntent: null,
                        currentRefetch: null,
                    },
                    trackPageview
                );

            this.setState(
                {
                    loadingIntent: intent || loadingIntents.HARD_REFRESH,
                    pendingLoadingIntent: null,
                    currentRefetch: this.props.relay.refetch(refetchVariables, null, onComplete),
                },
                trackPageview
            );
        }
    };

    render() {
        const { locationFilter, sortBy, page, showOnlyTpnFirms, showTestFirms, relay, viewer } =
            this.props;
        const { error, contactModalRecipient, isContactModalOpen, loadingIntent } = this.state;
        return (
            <div className={styles.wrapper}>
                <DesignerDirectoryHeader
                    locationFilter={locationFilter}
                    onLocationFilterChange={this.onLocationFilterChange}
                    onSortByChange={this.onSortByChange}
                    sortBy={sortBy}
                    showOnlyTpnFirms={showOnlyTpnFirms}
                    showTestFirms={showTestFirms}
                    viewer={viewer}
                />
                <DesignerDirectoryContent
                    error={error}
                    locationFilter={locationFilter}
                    handleContact={this.onContactClick}
                    handlePaginate={this.onPageChange}
                    loadingIntent={loadingIntent}
                    page={page}
                    sortBy={sortBy}
                    showOnlyTpnFirms={showOnlyTpnFirms}
                    showTestFirms={showTestFirms}
                    viewer={viewer}
                />
                <DesignerDirectoryContactModal
                    firmEmail={contactModalRecipient}
                    handleClose={() => {
                        this.setState({
                            isContactModalOpen: false,
                        });
                    }}
                    isOpen={isContactModalOpen}
                    relayEnvironment={relay ? relay.environment : {}}
                />
            </div>
        );
    }
}
export const DesignerDirectoryWrapper = createRefetchContainer(
    DesignerDirectoryWrapperComponent,
    {
        viewer: graphql`
            fragment DesignerDirectoryWrapper_viewer on Viewer {
                ...DesignerDirectoryHeader_viewer
                ...DesignerDirectoryContent_viewer
            }
        `,
    },
    designerDirectoryRootQuery
);
